import { formatCurrency, formatDisplayDate, formatInteger, formatPercent } from "../formatters";

export interface BrandDailyRow {
  date: string;
  brand: string;
  qualified: number;
  reported: number;
  paid: number;
  income: number;
  epq: number;
  epp: number;
  internalDup: number;
  brandDup: number;
  failure: number;
  initial: number;
  pending: number;
}

// Maps one row of the live brandsDailyId query's `query_output` array
// (see OptifundDashboardEndPointLogic.runQuery, which unwraps query_output
// before this runs) into the table/KPI/chart-ready shape. Per the Brands tab
// update spec (docs/mockups/BRANDS-tab-update-spec.md Section 4.0), this
// query's id is unchanged but its output now also carries Internal_Duplication
// and Brand_Duplication.
export function mapBrandDailyApiRow(raw: any): BrandDailyRow {
  return {
    date: raw?.activity_date,
    brand: raw?.brand_name,
    qualified: raw?.qualified_leads ?? 0,
    reported: raw?.successfully_reported_leads ?? 0,
    paid: raw?.paid_leads ?? 0,
    income: raw?.total_income ?? 0,
    epq: raw?.EPQ ?? 0,
    epp: raw?.EPP ?? 0,
    internalDup: raw?.Internal_Duplication ?? 0,
    brandDup: raw?.Brand_Duplication ?? 0,
    failure: raw?.Failure_reported_leads ?? 0,
    initial: raw?.Initial_reported_leads ?? 0,
    pending: raw?.Pending_reported_leads ?? 0,
  };
}

// Mirrors BrandsDailyTable.tsx's TABLE_HEADERS labels/column order and cell
// formatting exactly, so the exported CSV matches what's on screen. Column
// order here is Failure, then Initial, then Pending - the spec calls this
// out explicitly as different from the Monthly table's order below.
export const BRANDS_DAILY_CSV_HEADERS = [
  "Date",
  "Brand",
  "Qualified",
  "Reported",
  "Paid",
  "Total income",
  "EPQ",
  "EPP",
  "Internal Dup.",
  "Brand Dup.",
  "Failure",
  "Initial",
  "Pending",
];

export function toBrandsDailyCsvRow(row: BrandDailyRow): Array<string | number> {
  return [
    formatDisplayDate(row.date),
    row.brand,
    formatInteger(row.qualified),
    formatInteger(row.reported),
    formatInteger(row.paid),
    formatCurrency(row.income),
    formatCurrency(row.epq),
    formatCurrency(row.epp),
    formatInteger(row.internalDup),
    formatInteger(row.brandDup),
    formatInteger(row.failure),
    formatInteger(row.initial),
    formatInteger(row.pending),
  ];
}

export interface BrandMonthlyRow {
  brand: string;
  qualified: number;
  reported: number;
  paid: number;
  income: number;
  epq: number;
  epp: number;
  internalDup: number;
  brandDup: number;
  failure: number;
  pending: number;
  initial: number;
}

// Maps one row of the live brandsMonthlyId query's `query_output` array.
// This query (selected by the Month/Year filter) replaces the old MTD query
// per the Brands tab update spec, Section 3/4.1.
export function mapBrandMonthlyApiRow(raw: any): BrandMonthlyRow {
  return {
    brand: raw?.brand_name,
    qualified: raw?.qualified_leads ?? 0,
    reported: raw?.successfully_reported_leads ?? 0,
    paid: raw?.paid_leads ?? 0,
    income: raw?.total_income ?? 0,
    epq: raw?.EPQ ?? 0,
    epp: raw?.EPP ?? 0,
    internalDup: raw?.Internal_Duplication ?? 0,
    brandDup: raw?.Brand_Duplication ?? 0,
    failure: raw?.Failure_reported_leads ?? 0,
    pending: raw?.Pending_reported_leads ?? 0,
    initial: raw?.Initial_reported_leads ?? 0,
  };
}

// Mirrors BrandsMonthlyTable.tsx's TABLE_HEADERS labels/column order and cell
// formatting exactly, so the exported CSV matches what's on screen. Column
// order here is Failure, then Pending, then Initial - different from the
// Daily table's order above (spec Section 4.0/4.1).
export const BRANDS_MONTHLY_CSV_HEADERS = [
  "Brand",
  "Qualified Leads",
  "Reported",
  "Paid",
  "Total income",
  "EPQ",
  "EPP",
  "Internal Dup.",
  "Brand Dup.",
  "Failure Reported Leads",
  "Pending",
  "Initial",
];

export function toBrandsMonthlyCsvRow(row: BrandMonthlyRow): Array<string | number> {
  return [
    row.brand,
    formatInteger(row.qualified),
    formatInteger(row.reported),
    formatInteger(row.paid),
    formatCurrency(row.income),
    formatCurrency(row.epq),
    formatCurrency(row.epp),
    formatInteger(row.internalDup),
    formatInteger(row.brandDup),
    formatInteger(row.failure),
    formatInteger(row.pending),
    formatInteger(row.initial),
  ];
}

export interface BrandReportAttemptRow {
  brand: string;
  attempts: number;
  reportedSuccess: number;
  successRate: number; // 0-1 ratio from the server
  reportedOther: number;
  utilization: number; // 0-1 ratio from the server
  notReceived: number;
  incomePerAttempt: number;
}

// Maps one row of the live brandsReportAttemptId query's `query_output`
// array (spec Section 4.2, new table). Unlike the Daily/Monthly queries,
// this one's raw fields are plain lowercase snake_case, not
// Capitalized_Snake_Case - confirmed against a real response sample.
// `income_from_brand` exists in the query output but is intentionally not
// mapped/shown - the spec calls this out explicitly as not required by the
// current design.
export function mapBrandReportAttemptApiRow(raw: any): BrandReportAttemptRow {
  return {
    brand: raw?.brand_name,
    attempts: raw?.report_attempts ?? 0,
    reportedSuccess: raw?.reported_success_to_brand ?? 0,
    successRate: raw?.success_rate ?? 0,
    reportedOther: raw?.reported_success_to_other_brand ?? 0,
    utilization: raw?.utilization_rate ?? 0,
    notReceived: raw?.not_received ?? 0,
    incomePerAttempt: raw?.income_per_attempt ?? 0,
  };
}

// Mirrors BrandsReportAttemptTable.tsx's TABLE_HEADERS labels/column order.
export const BRANDS_REPORT_ATTEMPT_CSV_HEADERS = [
  "Brand",
  "Report attempts",
  "Reported success",
  "Success rate",
  "Reported to other brand",
  "Utilization rate",
  "Not received",
  "Income / attempt",
];

export function toBrandsReportAttemptCsvRow(row: BrandReportAttemptRow): Array<string | number> {
  return [
    row.brand,
    formatInteger(row.attempts),
    formatInteger(row.reportedSuccess),
    formatPercent(row.successRate * 100),
    formatInteger(row.reportedOther),
    formatPercent(row.utilization * 100),
    formatInteger(row.notReceived),
    formatCurrency(row.incomePerAttempt),
  ];
}
